import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { ContentHeader } from "../shell/ContentHeader";
import { BulkActionsBar, type BulkAction } from "./BulkActionsBar";
import { EmptyState, type EmptyProps } from "./EmptyState";
import { InfoPanel } from "./InfoPanel";

export type Column<T> = {
  key: string;
  header: string;
  cell(row: T): ReactNode;
  align?: "end";
  width?: string;
};

type Props<T> = {
  title: string;
  breadcrumb?: string[];
  primaryAction?: ReactNode;
  filters?: ReactNode;
  columns: Column<T>[];
  rows: T[];
  getRowId(row: T): string;
  rowLabel?(row: T): string;
  selected: string[];
  onSelectedChange(ids: string[]): void;
  /** Muda junto com os filtros; quando muda, a seleção é limpa (o lote não pode excluir linhas que sumiram da tela). */
  selectionScope?: string;
  bulkActions?: BulkAction[];
  info: { count: number; totals?: { label: string; value: string }[] };
  empty: EmptyProps;
  loading?: boolean;
  error?: string;
  hasMore?: boolean;
  onLoadMore?(): void;
  onRowOpen?(row: T): void;
};

const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])";
const isTextControl = (t: HTMLElement) =>
  t instanceof HTMLSelectElement ||
  t instanceof HTMLTextAreaElement ||
  (t instanceof HTMLInputElement && t.type !== "checkbox");

export function ListPage<T>(p: Props<T>) {
  const ids = p.rows.map(p.getRowId);
  const all = ids.length > 0 && ids.every((id) => p.selected.includes(id));
  const some = ids.some((id) => p.selected.includes(id));
  const label = p.rowLabel ?? ((r: T) => String((r as { name?: string }).name ?? p.getRowId(r)));
  const allRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = some && !all;
  }, [some, all]);
  const scope = useRef(p.selectionScope);
  useEffect(() => {
    if (scope.current === p.selectionScope) return;
    scope.current = p.selectionScope;
    p.onSelectedChange([]);
  }, [p.selectionScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) =>
    p.onSelectedChange(p.selected.includes(id) ? p.selected.filter((x) => x !== id) : [...p.selected, id]);

  // Design doc §8: setas navegam entre linhas (mesma coluna de controles), espaço seleciona a linha (FE20).
  const onBodyKey = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    const t = e.target as HTMLElement;
    const tr = t.closest("tr");
    if (!tr || isTextControl(t)) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const sib = (
        e.key === "ArrowDown" ? tr.nextElementSibling : tr.previousElementSibling
      ) as HTMLElement | null;
      if (!sib) return;
      const idx = [...tr.querySelectorAll<HTMLElement>(FOCUSABLE)].indexOf(t);
      const targets = [...sib.querySelectorAll<HTMLElement>(FOCUSABLE)];
      (targets[idx] ?? targets[0])?.focus();
      e.preventDefault();
    } else if (e.key === " " && !(t instanceof HTMLInputElement || t instanceof HTMLButtonElement)) {
      const id = tr.dataset.rowId;
      if (id) {
        e.preventDefault();
        toggle(id);
      }
    }
  };

  return (
    <>
      <ContentHeader title={p.title} breadcrumb={p.breadcrumb} actions={p.primaryAction} />
      {p.filters}
      <BulkActionsBar
        count={p.selected.length}
        actions={p.bulkActions ?? []}
        onClear={() => p.onSelectedChange([])}
      />
      <div className="row g-3">
        <div className="col-xl-9">
          <div className="box card">
            {p.error ? (
              <div className="alert alert-danger m-3" role="alert">
                {p.error}
              </div>
            ) : p.rows.length === 0 && !p.loading ? (
              <EmptyState {...p.empty} />
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          ref={allRef}
                          type="checkbox"
                          className="form-check-input"
                          aria-label="Selecionar todos"
                          checked={all}
                          onChange={() => p.onSelectedChange(all ? [] : ids)}
                        />
                      </th>
                      {p.columns.map((c) => (
                        <th
                          key={c.key}
                          scope="col"
                          className={c.align === "end" ? "text-end" : undefined}
                          style={{ width: c.width }}
                        >
                          {c.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody onKeyDown={onBodyKey}>
                    {p.rows.map((r) => {
                      const id = p.getRowId(r);
                      const sel = p.selected.includes(id);
                      return (
                        <tr
                          key={id}
                          data-row-id={id}
                          className={sel ? "table-active" : undefined}
                          onDoubleClick={() => p.onRowOpen?.(r)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Selecionar ${label(r)}`}
                              checked={sel}
                              onChange={() => toggle(id)}
                            />
                          </td>
                          {p.columns.map((c) => (
                            <td key={c.key} className={c.align === "end" ? "text-end num" : undefined}>
                              {c.cell(r)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {p.loading && (
              <div className="p-3 text-center text-muted" role="status">
                Carregando…
              </div>
            )}
            {p.hasMore && !p.loading && (
              <div className="p-3 text-center">
                <button type="button" className="btn btn-outline-secondary" onClick={p.onLoadMore}>
                  Carregar mais
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="col-xl-3">
          <InfoPanel count={p.info.count} totals={p.info.totals} />
        </div>
      </div>
    </>
  );
}
